/**
 * User and authentication type definitions
 */

export enum Department {
  CLIENT = 'CLIENT',
  PLANNER = 'PLANNER',
  ADVISOR = 'ADVISOR',
  SPECIALIST = 'SPECIALIST',
  // Same permission level as SPECIALIST (3) — not a seniority tier below or
  // above it. Position in this enum/list is declaration order only, not a
  // ranking (sc-1212 follow-up, confirmed with Tyler).
  ANALYST = 'ANALYST',
  DIRECTOR = 'DIRECTOR',
  ADMIN = 'ADMIN',
}

export type User = {
  id: string
  keycloakId?: string // Keycloak subject — set on User objects from the backend /users API; NOT populated on the AuthContext user (use its `id`, which already holds the sub)
  username?: string
  email: string
  firstName: string
  lastName: string
  picture?: string | null
  databaseId?: string // Database ID from our backend
  department?: Department // Department from our backend
  rightCapitalId?: string | null // RightCapital user ID
}

// Full user type from /users API endpoint (admin view)
export type AdminUser = {
  id: string
  keycloakId: string
  email: string
  firstName: string
  lastName: string
  title: string | null
  bio: string | null
  picture: string | null
  phoneNumber: string | null
  department: Department
  isActive: boolean
  deactivatedAt: string | null
  deletedAt: string | null
  createdAt: string
  updatedAt: string
  // HubSpot fields
  hubspotOwnerId: string | null
  hubspotUserId: string | null
  hubspotContactId: string | null
  hubspotObjectId: string | null
  hubspotSyncedAt: string | null
  // RightCapital fields
  rightCapitalId: string | null
  rightCapitalHouseholdId: string | null
  // Profile
  profilePictureBucketKey: string | null
  syncProfilePictureWithGoogle: boolean
  emailNotificationsEnabled: boolean
}

export type UpdateUserPayload = Partial<
  Pick<
    AdminUser,
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'title'
    | 'bio'
    | 'phoneNumber'
    | 'rightCapitalId'
    | 'rightCapitalHouseholdId'
    | 'syncProfilePictureWithGoogle'
  >
>

export type HubSpotSyncResponse = {
  success: boolean
  message: string
  email: string
  syncedAt: string
  errorCode?: string
}

/**
 * User augmented with their HubSpot owner ID — returned by the
 * /users/hubspot-owners endpoint used by the manual DG-form Deal linking
 * flow (#518). Extends `User` so it can feed directly into `UserSelector`.
 */
export interface HubspotOwner extends User {
  picture: string | null
  hubspotOwnerId: string
}

export type AuthTokens = {
  token: string
  refreshToken: string
  idToken?: string
}

export type AuthState = {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  tokens: AuthTokens | null
}

/**
 * Keycloak User API Response Types
 */
export type KeycloakUserRole = {
  id: string
  name: string
}

/**
 * The signed-in user, from GET /me.
 *
 * `permissions` is effective, not literal: an ADMIN comes back holding the full
 * catalog rather than an empty list, which is what lets a caller check access
 * with a single lookup instead of repeating an `isAdmin ||` special case
 * everywhere (sc-1215).
 */
export type CurrentUser = {
  id: string
  keycloakId: string
  email: string
  firstName: string
  lastName: string
  department: Department
  isAdmin: boolean
  permissions: Array<string>
}
